import React, { useMemo } from 'react';
import i18n from '../../../../../i18n';
import { fmt, formatMoney } from '../../../data/metrics';
import type { DrillPosHours, DrillPosShiftRow } from '../../../types';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';
import { countLabel, hourLabel, hourRange } from './substanceFormat';

const T = 'performance:drillPos.hours';

function titleFor(kind: DrillPosHours['valueKind']): string {
  if (kind === 'perGuest') return i18n.t(`${T}.titlePerGuest`);
  if (kind === 'perSeatHour') return i18n.t(`${T}.titlePerSeatHour`);
  return i18n.t(`${T}.titleRevenue`);
}

function basisNote(data: DrillPosHours): string {
  if (data.fallback) return i18n.t(`${T}.fallback`);
  if (data.valueKind === 'perGuest') return i18n.t(`${T}.basisPerGuest`);
  if (data.valueKind === 'perSeatHour') {
    const seats = fmt.num(data.seats ?? 0);
    const days = fmt.num(data.days ?? 0);
    return data.openHours
      ? i18n.t(`${T}.basisPerSeatHour`, { seats, days, hours: data.openHours })
      : i18n.t(`${T}.basisPerSeatHourNoHours`, { seats, days });
  }
  return i18n.t(`${T}.basisRevenue`);
}

export function shiftRowLabel(row: DrillPosShiftRow, basis: 'shift' | 'daypart'): string {
  if (row.key === '_outside') return i18n.t(`${T}.outsideShifts`);
  if (basis === 'daypart') return i18n.t(`performance:drillPos.daypart.${row.key}`);
  return row.label ?? row.key;
}

function axisNumber(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1000) return `${(v / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function basketsN(count: number): string {
  return countLabel(`${T}.baskets`, count);
}

function guestsN(count: number): string {
  return countLabel(`${T}.guests`, count);
}

export function DrillPosHoursSection({
  data,
  unit,
}: {
  metricKey: string;
  data: DrillPosHours;
  /** Payload currency (code or symbol). */
  unit?: string | null;
}) {
  const money = (v: number) => formatMoney(v, unit || undefined);
  const perGuest = data.valueKind === 'perGuest';
  const title = titleFor(data.valueKind);
  const shifts = data.byShift?.rows ?? [];
  const shiftBasis = data.byShift?.basis ?? 'daypart';
  const shiftMax = Math.max(...shifts.map(s => s.revenue), 0.0001);
  const meta = perGuest
    ? `${guestsN(data.guests ?? 0)} · ${basketsN(data.baskets)}`
    : basketsN(data.baskets);

  const chartData = data.hours.map(h => ({
    name: hourLabel(h.hour),
    fullName: hourRange(h.hour),
    value: h.value,
  }));

  const notes = (
    <>
      <p className="ddrill-tile-note">{basisNote(data)}</p>
      {data.filtersIgnored ? (
        <p className="ddrill-tile-note">{i18n.t(`${T}.filtersIgnored`)}</p>
      ) : null}
    </>
  );

  const inspector = useMemo(
    () => (
      <>
        <table className="dsub-table">
          <thead>
            <tr>
              <th>{i18n.t(`${T}.colHour`)}</th>
              <th className="num">{title}</th>
              {data.valueKind !== 'revenue' ? (
                <th className="num">{i18n.t(`${T}.colRevenue`)}</th>
              ) : null}
              <th className="num">n</th>
              <th className="num">{i18n.t(`${T}.colShare`)}</th>
            </tr>
          </thead>
          <tbody>
            {data.hours.map(h => (
              <tr key={h.hour}>
                <td>{hourRange(h.hour)}</td>
                <td className="num">{money(h.value)}</td>
                {data.valueKind !== 'revenue' ? (
                  <td className="num">{money(h.revenue)}</td>
                ) : null}
                <td className="num">
                  {perGuest && h.guests != null
                    ? `${guestsN(h.guests)} · ${basketsN(h.baskets)}`
                    : basketsN(h.baskets)}
                </td>
                <td className="num">{h.sharePct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        {shifts.length ? (
          <>
            <div className="dcrux-col-lab">
              {shiftBasis === 'shift'
                ? i18n.t(`${T}.byShift`)
                : i18n.t(`${T}.byDaypart`)}
            </div>
            <table className="dsub-table">
              <tbody>
                {shifts.map(s => (
                  <tr key={s.key}>
                    <td>
                      {shiftRowLabel(s, shiftBasis)}
                      {s.startMin != null && s.endMin != null ? (
                        <span className="dsub-n">
                          {' '}
                          {fmt.clock(s.startMin)}–{fmt.clock(s.endMin)}
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{money(s.revenue)}</td>
                    <td className="num">{basketsN(s.baskets)}</td>
                    <td className="num">
                      {i18n.t(`${T}.avgBasket`, { value: money(s.avgBasket) })}
                    </td>
                    <td className="num">{s.sharePct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="ddrill-tile-note">
              {shiftBasis === 'shift'
                ? i18n.t(`${T}.shiftBasis`)
                : i18n.t(`${T}.daypartBasis`)}
            </p>
          </>
        ) : null}
        {notes}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, unit],
  );

  if (!data.hours.length) return null;

  return (
    <DrillTile
      sectionId="pos-hours"
      span={8}
      title={title}
      meta={meta}
      inspector={inspector}
    >
      <DrillBarChart
        data={chartData}
        // Axis ticks stay unit-free ("60k"): "CHF 60k" does not fit the axis.
        fmtVal={axisNumber}
        labelFmt={money}
        height={150}
      />
      <p className="dsub-n">
        {i18n.t(`${T}.peak`, { hour: hourRange(data.peakHour) })}
      </p>
      {shifts.length ? (
        <div className="dsub-shifts">
          <div className="dcrux-col-lab">
            {shiftBasis === 'shift'
              ? i18n.t(`${T}.byShift`)
              : i18n.t(`${T}.byDaypart`)}
          </div>
          <div className="dcrux-si-list">
            {shifts.map(s => (
              <div className="dcrux-si-row" key={s.key}>
                <span className="dcrux-si-name" title={shiftRowLabel(s, shiftBasis)}>
                  {shiftRowLabel(s, shiftBasis)}
                </span>
                <span className="dcrux-si-track">
                  <span
                    className="dcrux-si-fill"
                    style={{ width: `${(s.revenue / shiftMax) * 100}%` }}
                  />
                </span>
                <span className="dcrux-si-val">
                  {money(s.revenue)} · {s.sharePct}%{' '}
                  <span className="dsub-n">· {basketsN(s.baskets)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {notes}
    </DrillTile>
  );
}
